import {
  FORM_DRAFT_EXPIRY_DAYS,
  FORM_DRAFT_MAX_BYTES,
  FORM_DRAFT_STORAGE_PREFIX,
  FORM_DRAFT_STORAGE_VERSION,
} from "./form-draft-constants";
import type { LocalFormDraft } from "./types/form-draft-types";

const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/** Returns a copy of `data` without the given keys. Returns `data` unchanged when there's nothing to drop. */
function redactKeys(data: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  if (keys.length === 0) return data;
  const redacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!keys.includes(key)) redacted[key] = value;
  }
  return redacted;
}

function isPlainDraftDataShape(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  for (const key of Object.keys(value)) {
    if (FORBIDDEN_KEYS.has(key)) return false;
  }
  return true;
}

function isValidLocalDraft(value: unknown): value is LocalFormDraft {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.version !== "number" || v.version !== FORM_DRAFT_STORAGE_VERSION) return false;
  if (typeof v.currentPage !== "number" || !Number.isInteger(v.currentPage) || v.currentPage < 0) return false;
  if (typeof v.createdAt !== "number" || !Number.isFinite(v.createdAt)) return false;
  if (typeof v.updatedAt !== "number" || !Number.isFinite(v.updatedAt)) return false;
  if (typeof v.expiresAt !== "number" || !Number.isFinite(v.expiresAt)) return false;
  if (!isPlainDraftDataShape(v.draftData)) return false;
  return true;
}

/**
 * Generates a localStorage key for a form draft.
 * @param formId - The form ID
 * @param eventId - Optional event ID for event-specific drafts
 * @returns The storage key string
 */
export function getFormDraftStorageKey(formId: number, eventId?: number | null): string {
  const eventSuffix = eventId ? `_${eventId}` : "";
  return `${FORM_DRAFT_STORAGE_PREFIX}${formId}${eventSuffix}`;
}

/**
 * Reads a form draft from localStorage. Returns null if the entry is missing,
 * expired, malformed, or fails shape validation (e.g. prototype-polluting keys).
 * @param formId - The form ID
 * @param eventId - Optional event ID for event-specific drafts
 */
export function readLocalFormDraft(formId: number, eventId?: number | null): LocalFormDraft | null {
  if (typeof window === "undefined") return null;

  try {
    const key = getFormDraftStorageKey(formId, eventId);
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (!isValidLocalDraft(parsed) || Date.now() > parsed.expiresAt) {
      clearLocalFormDraft(formId, eventId);
      return null;
    }

    return parsed;
  } catch {
    clearLocalFormDraft(formId, eventId);
    return null;
  }
}

/**
 * Saves a form draft to localStorage. No-op when the payload exceeds FORM_DRAFT_MAX_BYTES
 * so a runaway form can't fill the user's quota.
 *
 * localStorage is unencrypted, so sensitive values (phone/email) must never be
 * written here as clear text — `redactedKeys` is dropped before serialization.
 * This keeps "no PII in localStorage" an invariant of the storage layer rather
 * than something every caller must remember.
 *
 * @param formId - The form ID
 * @param data - Form field values
 * @param currentPage - Current page index (>= 0)
 * @param eventId - Optional event ID for event-specific drafts
 * @param redactedKeys - Field keys (labels) whose values must not be persisted
 */
export function saveLocalFormDraft(
  formId: number,
  data: Record<string, unknown>,
  currentPage: number,
  eventId?: number | null,
  redactedKeys: string[] = []
): void {
  if (typeof window === "undefined") return;

  try {
    const key = getFormDraftStorageKey(formId, eventId);
    const draftData = redactKeys(data, redactedKeys);
    const now = Date.now();
    const expiresAt = now + FORM_DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    // Preserve createdAt from existing draft (best effort).
    let createdAt = now;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          const existingCreatedAt = (parsed as Record<string, unknown>).createdAt;
          if (typeof existingCreatedAt === "number" && Number.isFinite(existingCreatedAt)) {
            createdAt = existingCreatedAt;
          }
        }
      } catch {
        // Ignore parse errors — fall back to now().
      }
    }

    const draft: LocalFormDraft = {
      version: FORM_DRAFT_STORAGE_VERSION,
      draftData,
      currentPage,
      createdAt,
      updatedAt: now,
      expiresAt,
    };

    const serialized = JSON.stringify(draft);
    if (serialized.length > FORM_DRAFT_MAX_BYTES) return;
    localStorage.setItem(key, serialized);
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Removes a form draft from localStorage.
 * @param formId - The form ID
 * @param eventId - Optional event ID for event-specific drafts
 */
export function clearLocalFormDraft(formId: number, eventId?: number | null): void {
  if (typeof window === "undefined") return;

  try {
    const key = getFormDraftStorageKey(formId, eventId);
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}
